import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
// import utc from "dayjs/plugin/utc";
// import timezone from "dayjs/plugin/timezone";
import { Container, Text } from "@pixi/react";
import { TextStyle } from "pixi.js";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { Settings } from "../../../../settings";
import { PixiSettings } from "../../../../pixiSettings";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
} from "../../../../pixiSettings";
import { outlineShadow } from "../../../../pixiSettings";
import { useSoundButtonLayout } from "../Buttons/useSoundButtonLayout";

// ── Beside the sound button, at a fixed gap (2026-10-06) ──
// Where the clock sits NEXT TO the sound button (desktop: to its left; mobile
// landscape: to its right), its near edge is placed this far from the
// button's edge — a share of the button's width, so it scales with it — and
// the text grows AWAY from the button. Any game name fits with no per-game
// positioning. Centred layouts (portrait, tablet landscape) are unchanged.
// const CLOCK_GAP_RATIO = 0.3;
// const CLOCK_GAP_RATIO = 0.2; // a tiny bit closer to the button (2026-10-06)
const CLOCK_GAP_RATIO = 0.12; // a bit closer again (2026-10-06)

// // Extend dayjs with timezone plugins
// dayjs.extend(utc);
// dayjs.extend(timezone);

// const Clock = ({ timeZone = "Europe/Belgrade", toolbar }) => {
// The device's own time, not a fixed zone (QA: a player abroad saw Serbian
// time). From 3 Orbs of Zeus, f1369eef, 2026-09-30.
const Clock = ({ toolbar }) => {
  const {
    device,
    orientation,
    getResponsiveConfig,
    uiSymbolScale,
    isMobileSmall,
  } = useDimensions();
  const [time, setTime] = useState(dayjs());

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(dayjs());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Format the time string — shows game name and time
  const formattedTime = useMemo(
    // () => `${Settings.APP.GAME_NAME} | ${time.tz(timeZone).format("HH:mm")}`,
    // [time, timeZone],
    () => `${Settings.APP.GAME_NAME} | ${time.format("HH:mm")}`,
    [time],
  );

  // Get button dimensions using the same system as other secondary buttons
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // The sound button the clock sits beside — the same numbers it draws from.
  const { buttonConfig: soundButton, buttonPosition: soundPosition } =
    useSoundButtonLayout(toolbar);

  // Unified clock configuration (font multiplier, x, y positions)
  const clockConfig = useMemo(() => {
    let fontMultiplier = 1.0;
    let x = 0;
    let y = 0;
    // "left" = the clock ENDS a gap left of the sound button (anchor right
    // edge), "right" = it STARTS a gap right of it (anchor left edge), null =
    // centred on x as before.
    let side = null;

    if (isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.0;
        x = toolbar.width / 2;
        y = buttonConfig.height * 0.3;
      } else {
        fontMultiplier = 0.875;
        // x = buttonConfig.width * 2.75;
        // y = buttonConfig.height * 0.6;
        side = "right";
      }
    } else if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.0;
        x = toolbar.width / 2;
        y = buttonConfig.height * 0.225;
      } else {
        fontMultiplier = 0.9;
        // x = buttonConfig.width * 2.75;
        // y = buttonConfig.height * 0.7;
        side = "right";
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.55;
        x = toolbar.width / 2;
        y = buttonConfig.height * 0.3;
      } else {
        fontMultiplier = 1.1;
        // x = toolbar.width / 2;
        // y = buttonConfig.height * 0.4;
        // Beside the sound button, as on desktop — not centred (3 Orbs of
        // Zeus e87f8036, 2026-10-08). Tablet landscape has the button on the
        // right like desktop, so the clock ends a gap LEFT of it.
        side = "left";
      }
    } else {
      // Desktop
      fontMultiplier = 1.0;
      // x = toolbar.width - buttonConfig.width * 4.25;
      // y = buttonConfig.height * 0.785;
      side = "left";
    }

    // Beside the button: on its centre line, a fixed gap from its edge.
    if (side) {
      const gap = soundButton.width * CLOCK_GAP_RATIO;
      const halfButton = soundButton.width / 2;
      y = soundPosition.y;
      x =
        side === "left"
          ? soundPosition.x - halfButton - gap
          : soundPosition.x + halfButton + gap;
    }

    // return { fontMultiplier, x, y };
    return { fontMultiplier, x, y, side };
  }, [
    device,
    orientation,
    isMobileSmall,
    toolbar.width,
    // buttonConfig.width, (only the beside-the-button layouts used it)
    buttonConfig.height,
    soundButton.width,
    soundPosition.x,
    soundPosition.y,
  ]);
  // The text's anchor: its near edge on x when beside the button.
  const textAnchor = useMemo(
    () =>
      clockConfig.side === "left"
        ? { x: 1, y: 0.5 }
        : clockConfig.side === "right"
          ? { x: 0, y: 0.5 }
          : { x: 0.5, y: 0.5 },
    [clockConfig.side],
  );

  // Calculate font size — snap to even for clean DPR rasterization
  const baseFontSize = 28;
  const FONT_SIZE = useMemo(() => {
    const responsiveFontSize = baseFontSize * clockConfig.fontMultiplier;
    const raw = responsiveFontSize * uiSymbolScale;
    return Math.round(raw / 2) * 2;
  }, [clockConfig.fontMultiplier, uiSymbolScale]);

  // Text style
  const clockStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: FONT_SIZE,
        fontWeight: "600",
        fill: 0xffffff,
        align: "center",
        resolution: window.devicePixelRatio || 1,
        // ...TEXT_STYLE_CONFIG.OUTLINE,
        ...outlineShadow(FONT_SIZE), // shadow scales with the text
      }),
    [FONT_SIZE],
  );

  // Clock position
  const clockPosition = useMemo(
    () => ({
      x: Math.round(clockConfig.x),
      y: Math.round(clockConfig.y),
    }),
    [clockConfig.x, clockConfig.y],
  );

  return (
    <Container
      name="secondary_clock"
      x={clockPosition.x}
      y={clockPosition.y}
      zIndex={3}
    >
      <Text
        text={formattedTime}
        style={clockStyle}
        // anchor={0.5}
        anchor={textAnchor}
        roundPixels={true}
        x={0}
        y={0}
        accessible={true}
        accessibleTitle={formattedTime}
      />
    </Container>
  );
};

export default Clock;
